import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import DataPill from "@components/pills/DataPill";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import TableActionButton from "@components/buttons/TableActionButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import KycDumpSlider from "@components/sliders/KycDumpSlider";
import { balanceApis } from "@services/apis/Balances";
import { DebitFreezeRequest } from "@services/models/Balances/DebitFreezeModel";
import {
  GetBalanceData,
  GetBalanceResponse,
} from "@services/models/Balances/GetBalanceModel";
import { KycData } from "@utils/clxm/kycdata";
import { ColDef } from "ag-grid-community";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isPaysonicHost } from "src/config/deploymentConfig";
import ConstantFunctions from "../../../utils/constants_functions";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import MissingUserTransactionSlider from "../components/MissingUserTransactionSlider";

const UserBalances = ({ embedded = false }: { embedded?: boolean }) => {
  const constantFunctions = new ConstantFunctions();
  const isPaysonic = isPaysonicHost();
  const gridRef = useRef<any>(null);
  const loadingRef = useRef<boolean>(false);

  const [balances, setBalances] = useState<GetBalanceData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [forMerchant, setForMerchant] = useState<"Users" | "Merchants">(
    "Users",
  );
  const rowsPerPage = 10000;

  const [openMissingTransactionSlider, setOpenMissingTransactionSlider] =
    useState<boolean>(false);

  const [isKycDumpOpen, setIsKycDumpOpen] = useState(false);
  const [kycData, setKycData] = useState<KycData>({
    idDump: undefined,
    poaDump: undefined,
    poiFrontUrl: "",
    poaFrontUrl: "",
    poaBackUrl: "",
  });

  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [hasRequestedList, setHasRequestedList] = useState(false);
  const hasRequestedListRef = useRef(false);
  const { handleError, handleSuccess } = useSnackbarMessage();

  const columnDefs: ColDef[] = useMemo(() => {
    const baseColumns: ColDef[] = [
      {
        headerName: "UID",
        field: "userId",
        flex: 0.6,
        minWidth: 60,
        maxWidth: 100,
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center justify-between">
              <span className="flex w-[40px]">{params.data.userId}</span>
              {params.data.userId && (
                <CopyButton
                  copyText={params.data.userId.toString()}
                  copyMessage="User ID copied!"
                  buttonSize="14px"
                  copyIconSize={10}
                  borderRadius="2px"
                />
              )}
            </div>
          );
        },
      },
    ];

    // Add conditional columns based on forMerchant
    if (forMerchant !== "Merchants") {
      baseColumns.push({
        headerName: "User Name",
        field: "username",
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      });
    } else {
      baseColumns.push(
        {
          headerName: "MID",
          field: "merchantId",
          flex: 0.6,
          minWidth: 60,
          maxWidth: 100,
          headerClass: "ag-header-cell-custom",
          cellClass: "ag-cell-custom",
          filter: "agNumberColumnFilter",
          cellRenderer: (params: any) => {
            return (
              <div className="flex items-center justify-between">
                <span className="flex-1">{params.data.merchantId}</span>
                {params.data.merchantId && (
                  <CopyButton
                    copyText={params.data.merchantId.toString()}
                    copyMessage="Merchant ID copied!"
                    buttonSize="14px"
                    copyIconSize={10}
                    borderRadius="2px"
                  />
                )}
              </div>
            );
          },
        },
        {
          headerName: "Merchant Name",
          field: "merchantName",
          flex: 1.2,
          minWidth: 110,
          maxWidth: 160,
          headerClass: "ag-header-cell-custom",
          cellClass: "ag-cell-custom",
          filter: "agTextColumnFilter",
          wrapText: true,
          autoHeight: true,
          cellStyle: {
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: "1.2",
          },
        },
        {
          headerName: "Category",
          field: "accountCategory",
          flex: 1.2,
          minWidth: 110,
          maxWidth: 160,
          headerClass: "ag-header-cell-custom",
          cellClass: "ag-cell-custom",
          filter: "agTextColumnFilter",
          valueFormatter: (params) => params.value ?? "- -",
          wrapText: true,
          autoHeight: true,
          cellStyle: {
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: "1.2",
          },
        },
      );
    }

    // Common columns
    baseColumns.push(
      {
        headerName: "Email",
        field: "emailId",
        flex: 1.5,
        minWidth: 140,
        maxWidth: 200,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center justify-between max-w-[200px]">
              <span className="flex-1 truncate pr-1">
                {params.data.emailId}
              </span>
              <CopyButton
                copyText={params.data.emailId}
                copyMessage="Email copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "USDT",
        field: "usdtBalance",
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <div className="flex flex-row items-center gap-[4px]">
              <img src={data.usdtImg} height={10} width={10} alt="USDT" />
              {constantFunctions.formatCurrency(Number(data.usdtBalance))}
            </div>
          );
        },
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        comparator: (valueA: string, valueB: string) => {
          return Number(valueA) - Number(valueB);
        },
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
      },
      {
        headerName: "USDC",
        field: "usdcBalance",
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <div className="flex flex-row items-center gap-[4px]">
              <img src={data.usdcImg} height={10} width={10} alt="USDC" />
              {constantFunctions.formatCurrency(Number(data.usdcBalance))}
            </div>
          );
        },
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        comparator: (valueA: string, valueB: string) => {
          return Number(valueA) - Number(valueB);
        },
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
      },
      {
        headerName: "DAI",
        field: "daiBalance",
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <div className="flex flex-row items-center gap-[4px]">
              <img src={data.daiImg} height={10} width={10} alt="DAI" />
              {constantFunctions.formatCurrency(Number(data.daiBalance))}
            </div>
          );
        },
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        comparator: (valueA: string, valueB: string) => {
          return Number(valueA) - Number(valueB);
        },
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
      },
    );

    // Add Escrow column only for Users
    if (forMerchant !== "Merchants") {
      baseColumns.push({
        headerName: "Escrow",
        field: "e_usdtBalance",
        flex: 1.1,
        minWidth: 100,
        maxWidth: 150,
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <div className="flex flex-row items-center gap-[4px]">
              <img
                src={data.e_usdtImg}
                height={10}
                width={10}
                alt="Escrow USDT"
              />
              {constantFunctions.formatCurrency(Number(data.e_usdtBalance))}
            </div>
          );
        },
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        comparator: (valueA: string, valueB: string) => {
          return Number(valueA) - Number(valueB);
        },
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
      });
    }

    // Add remaining common columns
    baseColumns.push(
      {
        headerName: "Total",
        field: "total",
        flex: 1.1,
        minWidth: 100,
        maxWidth: 150,
        cellRenderer: (props: any) => {
          const { data } = props;
          return constantFunctions.formatCurrency(Number(data.total));
        },
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        comparator: (valueA: string, valueB: string) => {
          return Number(valueA) - Number(valueB);
        },
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
      },
      {
        headerName: "Debit Freeze",
        field: "isDebitFrozen",
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <DataPill
              label={data.isDebitFrozen === 0 ? "NO" : "YES"}
              tone={data.isDebitFrozen === 0 ? "green" : "red"}
            />
          );
        },
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Action",
        field: "action",
        flex: 0.8,
        minWidth: 130,
        maxWidth: 160,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
        cellRenderer: (props: any) => {
          const { data } = props;
          return data.isDebitFrozen === 1 ? (
            <TableActionButton
              variant="success"
              icon="lock_open"
              loading={isButtonLoading}
              onClick={(event) => {
                event.stopPropagation();
                handldleDebitFreeze(
                  data.userId,
                  forMerchant === "Merchants" ? data.merchantId : undefined,
                  0,
                );
              }}
            >
              Unfreeze
            </TableActionButton>
          ) : (
            <TableActionButton
              variant="danger"
              icon="lock"
              loading={isButtonLoading}
              onClick={(event) => {
                event.stopPropagation();
                handldleDebitFreeze(
                  data.userId,
                  forMerchant === "Merchants" ? data.merchantId : undefined,
                  1,
                );
              }}
            >
              Freeze
            </TableActionButton>
          );
        },
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: false,
        sortable: false,
      },
    );

    // Add KYC column only for Users
    if (forMerchant !== "Merchants") {
      baseColumns.push({
        headerName: "KYC",
        field: "kyc",
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
        cellRenderer: (props: any) => {
          const { data } = props;
          const hasKycData = !!(
            data.poiDump ||
            data.poaDump ||
            data.poiImageUrl ||
            data.poaImageFrontUrl ||
            data.poaImageBackUrl
          );
          return (
            <LightCustomButton
              text="VIEW"
              textSize="10px"
              isActive={hasKycData}
              onClick={() => {
                setIsKycDumpOpen(true);
                setKycData({
                  idDump: data.poiDump,
                  poaDump: data.poaDump,
                  poiFrontUrl: data.poiImageUrl,
                  poaFrontUrl: data.poaImageFrontUrl,
                  poaBackUrl: data.poaImageBackUrl,
                });
              }}
              className="!h-[24px] !flex-none w-full flex items-center justify-center"
            />
          );
        },
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: false,
        sortable: false,
      });
    }

    return baseColumns;
  }, [forMerchant, constantFunctions, isButtonLoading]);

  const fetchBalanceList = useCallback(
    async (pageNumber = 1, append = false, search?: string) => {
      if (loadingRef.current) return;

      try {
        loadingRef.current = true;
        setLoading(true);
        const response: GetBalanceResponse = await balanceApis.getBalances({
          page: pageNumber,
          rows: rowsPerPage,
          search,
          forMerchant: isPaysonic ? false : forMerchant === "Merchants" ? 1 : 0,
          userAccountType: isPaysonic ? "cashier" : undefined,
        });

        if (response.data.length < rowsPerPage) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }

        setBalances((prev) =>
          append ? [...prev, ...response.data] : response.data,
        );
      } catch (err: any) {
        setError("Failed to fetch balance data");
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [forMerchant],
  );

  const resetListState = useCallback(() => {
    setBalances([]);
    setPage(1);
    setHasMore(true);
  }, []);

  const loadMoreBalances = useCallback(() => {
    if (!hasRequestedListRef.current || loadingRef.current || !hasMore) {
      return;
    }

    const nextPage = page + 1;
    setPage(nextPage);
    fetchBalanceList(nextPage, true, appliedSearchTerm || undefined);
  }, [appliedSearchTerm, fetchBalanceList, hasMore, page]);

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasRequestedListRef.current || !hasMore || loading) return;

      const displayedRowCount = params.api.getDisplayedRowCount();
      if (displayedRowCount === 0) return;

      const scrollTop = params.top;
      const scrollHeight = displayedRowCount * 48; // row height
      const clientHeight =
        params.api.getVerticalPixelRange().bottom -
        params.api.getVerticalPixelRange().top;

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMoreBalances();
      }
    },
    [hasMore, loading, loadMoreBalances],
  );

  useEffect(() => {
    if (!hasRequestedListRef.current) return;
    resetListState();
    fetchBalanceList(1, false, appliedSearchTerm || undefined);
  }, [appliedSearchTerm, fetchBalanceList, forMerchant, resetListState]);

  const handleLoadBalances = useCallback(() => {
    const normalizedSearchTerm = searchTerm.trim();
    hasRequestedListRef.current = true;
    setHasRequestedList(true);

    if (normalizedSearchTerm !== appliedSearchTerm) {
      setAppliedSearchTerm(normalizedSearchTerm);
      return;
    }

    resetListState();
    fetchBalanceList(1, false, normalizedSearchTerm || undefined);
  }, [
    appliedSearchTerm,
    fetchBalanceList,
    resetListState,
    searchTerm,
  ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = useCallback(() => {
    const normalizedSearchTerm = searchTerm.trim();
    hasRequestedListRef.current = true;
    setHasRequestedList(true);

    if (normalizedSearchTerm === appliedSearchTerm) {
      resetListState();
      fetchBalanceList(1, false, normalizedSearchTerm || undefined);
      return;
    }

    setAppliedSearchTerm(normalizedSearchTerm);
  }, [appliedSearchTerm, fetchBalanceList, resetListState, searchTerm]);

  const handleClearSearch = useCallback(() => {
    if (!searchTerm && !appliedSearchTerm) {
      return;
    }

    setSearchTerm("");

    if (!hasRequestedListRef.current) {
      return;
    }

    if (appliedSearchTerm === "") {
      resetListState();
      fetchBalanceList(1, false);
      return;
    }

    setAppliedSearchTerm("");
  }, [appliedSearchTerm, fetchBalanceList, resetListState, searchTerm]);

  const handleMerchantTypeChange = useCallback(
    (type: "Users" | "Merchants") => {
      if (forMerchant === type) {
        return;
      }

      setSearchTerm("");
      setAppliedSearchTerm("");
      setForMerchant(type);
    },
    [forMerchant],
  );

  const handldleDebitFreeze = async (
    userId: number,
    merchantId?: number,
    freeze?: number,
  ) => {
    try {
      setIsButtonLoading(true);
      const debitFreezeRequest: DebitFreezeRequest = {
        userId: userId,
        merchantId: merchantId,
        freeze: freeze,
      };

      const data = await balanceApis.debitFreezeAccount(debitFreezeRequest);

      // Update the local state to reflect the change
      setBalances((prevBalances) =>
        prevBalances.map((balance) => {
          if (
            balance.userId === userId &&
            (merchantId === undefined || balance.merchantId === merchantId)
          ) {
            return {
              ...balance,
              isDebitFrozen: freeze === 1 ? 1 : 0,
            };
          }
          return balance;
        }),
      );

      handleSuccess({ message: data.msg || "Status changed successfully" });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating status failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsButtonLoading(false);
    }
  };

  return (
    <>
      <div
        className={`flex min-h-0 flex-col gap-4 overflow-hidden bg-bg-page-body ${
          embedded ? "h-full min-h-0 flex-1 px-4 py-4" : "py-[20px]"
        }`}
        style={embedded ? undefined : { height: "calc(100dvh - 120px)" }}
      >
        <div
          className={`flex items-center justify-between gap-2 ${
            embedded ? "flex-wrap" : "flex-row"
          }`}
        >
          {!isPaysonic ? (
            <div className="flex space-x-3">
              {["Users", "Merchants"].map((type) => (
                <div
                  key={type}
                  onClick={
                    loading
                      ? undefined
                      : () =>
                          handleMerchantTypeChange(
                            type as "Users" | "Merchants",
                          )
                  }
                  className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                    loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                  } ${
                    forMerchant === type
                      ? "border-border-button-secondary bg-bg-button-secondary"
                      : "border-border-subtle bg-bg-page-body"
                  }`}
                >
                  {type}
                </div>
              ))}
            </div>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            {/* <LightCustomButton
              text="MISSING TRANSACTION?"
              isActive={true}
              onClick={() => setOpenMissingTransactionSlider(true)}
              textSize="12px"
            /> */}
            <LightCustomButton
              text={hasRequestedList ? "Reload" : "Load"}
              textSize="10px"
              isActive={!loading}
              onClick={handleLoadBalances}
              className="!h-[28px] !flex-none !px-[10px]"
            />
            <GlobalSearchInputField
              value={searchTerm}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              onClear={handleClearSearch}
              disabled={loading}
              isSearchApplied={Boolean(appliedSearchTerm)}
              placeholder="Search"
              className={embedded ? "w-[240px] min-w-[180px]" : ""}
            />
          </div>
        </div>
        <div className="min-h-0 flex-1">
          {loading && balances.length === 0 ? (
            <AgGridSkeleton
              columnDefs={columnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={gridRef}
              rowData={balances}
              columnDefs={columnDefs}
              onBodyScroll={onBodyScroll}
              getRowId={(params) =>
                `${params.data.userId}-${params.data.merchantId || "user"}`
              }
              error={error}
              overlayNoRowsTemplate={
                hasRequestedList
                  ? undefined
                  : "<span>Click Load to fetch balances</span>"
              }
            />
          )}
        </div>
        {(kycData.idDump ||
          kycData.poaDump ||
          kycData.poiFrontUrl ||
          kycData.poaFrontUrl ||
          kycData.poaBackUrl) && (
          <KycDumpSlider
            isOpen={isKycDumpOpen}
            onClose={() => setIsKycDumpOpen(false)}
            kycDumpData={kycData}
          />
        )}
      </div>
      <MissingUserTransactionSlider
        isOpen={openMissingTransactionSlider}
        onClose={() => setOpenMissingTransactionSlider(false)}
      />
    </>
  );
};

export default UserBalances;
